import { useConvexMutation } from '@convex-dev/react-query';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { api } from '~/convex/_generated/api';
import type { Id } from '~/convex/_generated/dataModel';

export type TranscriptionStatus =
  | 'idle'
  | 'connecting'
  | 'transcribing'
  | 'stopping'
  | 'finalizing';

export const isActive = (status: TranscriptionStatus) => {
  return status === 'connecting' || status === 'transcribing';
};

const STATUS_TRANSITIONS: Record<
  TranscriptionStatus,
  readonly TranscriptionStatus[]
> = {
  idle: ['connecting'],
  connecting: ['transcribing', 'stopping', 'idle'],
  transcribing: ['stopping', 'idle'],
  stopping: ['finalizing', 'idle'],
  finalizing: ['idle'],
};

type ActiveSegmentMeta = {
  seq: number;
  baseOffsetMs: number;
};

/**
 * Manages recording lifecycle status, epoch guards, and stop-in-flight lock.
 * Segment persistence is modeled directly through reportSegments.
 */
export const useRecordingSession = ({
  reportId,
}: {
  reportId: Id<'reports'>;
}) => {
  const [status, setStatus] = useState<TranscriptionStatus>('idle');
  const statusRef = useRef<TranscriptionStatus>('idle');
  const activeSegmentRef = useRef<ActiveSegmentMeta | null>(null);
  const stopInFlightRef = useRef<Promise<void> | null>(null);
  const sessionEpochRef = useRef(0);

  const { mutateAsync: startLiveSegment } = useMutation({
    mutationFn: useConvexMutation(api.recording.startLiveSegment),
  });

  const { mutateAsync: markRecordingFinalizing } = useMutation({
    mutationFn: useConvexMutation(api.recording.markRecordingFinalizing),
  });

  const { mutateAsync: cancelLiveSegment } = useMutation({
    mutationFn: useConvexMutation(api.recording.cancelLiveSegment),
  });

  const { mutateAsync: markRecordingFailed } = useMutation({
    mutationFn: useConvexMutation(api.recording.markRecordingFailed),
  });

  const transitionStatus = useCallback(
    (nextStatus: TranscriptionStatus, opts?: { force?: boolean }) => {
      const currentStatus = statusRef.current;
      if (currentStatus === nextStatus) return true;

      const isAllowed =
        opts?.force === true ||
        STATUS_TRANSITIONS[currentStatus].includes(nextStatus);

      if (!isAllowed) {
        console.warn(
          `Invalid transcription status transition: ${currentStatus} -> ${nextStatus}`,
        );
        return false;
      }

      statusRef.current = nextStatus;
      setStatus(nextStatus);
      return true;
    },
    [],
  );

  const beginEpoch = useCallback(() => {
    sessionEpochRef.current += 1;
    return sessionEpochRef.current;
  }, []);

  const invalidateEpoch = useCallback(() => {
    sessionEpochRef.current += 1;
    return sessionEpochRef.current;
  }, []);

  const isEpochCurrent = useCallback((epoch: number) => {
    return sessionEpochRef.current === epoch;
  }, []);

  const startSegment = useCallback(async () => {
    const result = await startLiveSegment({ reportId });
    activeSegmentRef.current = {
      seq: result.seq,
      baseOffsetMs: result.baseOffsetMs,
    };
    return result;
  }, [reportId, startLiveSegment]);

  const beginFinalizing = useCallback(async () => {
    await markRecordingFinalizing({ reportId });
  }, [markRecordingFinalizing, reportId]);

  const completeSegment = useCallback(() => {
    activeSegmentRef.current = null;
  }, []);

  const cancelSegment = useCallback(async () => {
    await cancelLiveSegment({ reportId });
    activeSegmentRef.current = null;
  }, [cancelLiveSegment, reportId]);

  const failSegment = useCallback(
    async (params: { code: string; message: string }) => {
      try {
        await markRecordingFailed({
          reportId,
          code: params.code,
          message: params.message,
        });
      } catch (failureError) {
        console.error('Failed to mark recording as failed:', failureError);
      } finally {
        activeSegmentRef.current = null;
      }
    },
    [markRecordingFailed, reportId],
  );

  const cleanupSegment = useCallback(() => {
    activeSegmentRef.current = null;
  }, []);

  return {
    status,
    statusRef,
    activeSegmentRef,
    stopInFlightRef,
    transitionStatus,
    beginEpoch,
    invalidateEpoch,
    isEpochCurrent,
    startSegment,
    beginFinalizing,
    completeSegment,
    cancelSegment,
    failSegment,
    cleanupSegment,
  };
};
